import { Control, useController, useFormContext } from "react-hook-form";

import { Label } from "src/base-components/Label";
import { Select } from "src/base-components/Select";
import { Textarea } from "src/base-components/Textarea";
import { type FileCategoryBase } from "src/cases/caseView/FileCategory";
import { CreateCaseFileUpload } from "src/cases/createCase/CreateCaseFileUpload";
import {
  CreateCaseFormValues,
  CreateCaseStep,
} from "src/cases/createCase/createCaseSteps";
import { FormStepWrapper } from "src/cases/templates/FormStepWrapper";

export type SourceOption = {
  key: string;
  value: string;
};

type CreateCaseContextStepProps = {
  sourceOptions: SourceOption[] | undefined;
  fileCategories: FileCategoryBase[];
  step: CreateCaseStep;
};

export const CreateCaseContextStep: React.FC<CreateCaseContextStepProps> = ({
  sourceOptions,
  fileCategories,
  step,
}) => {
  const form = useFormContext<CreateCaseFormValues>();
  const hasSource = !!sourceOptions?.length;

  const title = hasSource
    ? "Define the Case source and context"
    : step.formTitle;

  const description = hasSource
    ? "Select where this Case came from, note why you're opening it, and attach any supporting files"
    : "Note why you're opening this case, and attach any supporting files";

  return (
    <FormStepWrapper description={description} title={title}>
      <div className="flex flex-col gap-3">
        {hasSource && (
          <div className="flex flex-col">
            <Label required>Case source</Label>
            <CaseSourceSelect
              control={form.control}
              sourceOptions={sourceOptions}
            />
          </div>
        )}
        <div className="flex flex-col">
          <Label>Comment</Label>
          <Textarea
            data-loc="create-case-comment"
            placeholder="Add a comment"
            autoGrow
            fullWidth
            {...form.register("comment")}
          />
        </div>
        <div className="flex flex-col">
          <Label mb="mb-1">Files</Label>
          <p className="mb-2 font-inter-normal-12px text-gray-500">
            Attach supporting documents to this Case
          </p>
          <CreateCaseFileUpload fileCategories={fileCategories} />
        </div>
      </div>
    </FormStepWrapper>
  );
};

const CaseSourceSelect: React.FC<{
  control: Control<CreateCaseFormValues>;
  sourceOptions: SourceOption[] | undefined;
}> = ({ control, sourceOptions }) => {
  const { field } = useController({
    control,
    name: "source",
    rules: { required: true },
  });

  return (
    <Select
      dataLoc="create-case-source-dropdown"
      options={sourceOptions ?? []}
      placeholder="Select a source"
      value={field.value ?? ""}
      onChange={field.onChange}
    />
  );
};
